/**
 * /admin/exams/new and /admin/exams/:examId — the exam builder.
 *
 * The order of work is the order of the page: settings, then a paper, then a
 * preview, then schedule.
 *
 * An exam must exist as a draft before questions can be attached — `auto-pick`
 * and `questions` are both `/api/exams/:id/…` — so a new exam saves the
 * settings first and then reveals the paper section. That is also the safer
 * shape: the settings are never lost because the picking went sideways.
 *
 * What a started sitting freezes is the *paper*, not the exam. Once an attempt
 * leaves `not_started` the API rejects any change to the attached questions
 * (409), and the UI reflects that rather than letting the request fail — but
 * the settings around the paper stay editable, through `PATCH /api/exams/:id`.
 * They have to be: a mistyped title, a wrong date or a duration that turned out
 * to be too short used to mean rebuilding the exam from scratch, because the
 * builder disabled every field the moment the draft was saved and never called
 * the endpoint that could have fixed them.
 *
 * Only two settings lock, and only once someone is actually sitting the exam:
 * the subject (changing it would orphan the paper's questions) and the timing
 * mode (changing it moves the goalposts mid-sitting). Everything else — title,
 * schedule, window, duration, submit lock, syllabus — is editable at any
 * status, including `done`, which can itself be reopened.
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  Eye,
  Lock,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CopyButton,
  Dialog,
  Input,
  Select,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@/components/ui";
import { Difficulty } from "@exam/shared";
import type { AutoPickResponse, ExamQuestionDetail } from "@exam/shared";
import { DIFFICULTY_LABEL, labelFor } from "./lib/enumLabels";
import { useCatalog } from ".";
import {
  EMPTY_FILTERS,
  QuestionFilters,
  type QuestionFilterValues,
  toQuery,
} from "./components/QuestionFilters";
import { ExamActionsCard } from "./components/ExamActions";
import { ExamRosterCard } from "./components/ExamRoster";
import { QuestionMeta, blockersFor } from "./components/QuestionMeta";
import { QuestionView } from "./components/QuestionView";
import { ErrorNote, FixtureBanner, Loading, PageTitle } from "./components/Shell";
import { plainSummary } from "./QuestionsPage";
import { summariseWeeks } from "./ExamsPage";
import {
  attachQuestions,
  autoPick,
  createExam,
  fetchExamDetail,
  fetchQuestions,
  publishExam,
  updateExam,
} from "./data/client";
import {
  formatMarks,
  fromDateTimeLocal,
  toDateTimeLocal,
} from "./lib/format";
import { useAsync } from "./lib/useAsync";

export function ExamBuilderPage() {
  const { examId } = useParams();
  const isNew = examId === undefined;
  const catalog = useCatalog();
  const navigate = useNavigate();
  const { toast } = useToast();

  const detail = useAsync(
    () => fetchExamDetail(examId!, catalog),
    [examId, catalog],
    { enabled: !isNew },
  );

  return (
    <div className="space-y-4">
      <PageTitle
        title={isNew ? "New exam" : (detail.data?.exam.title ?? "Exam")}
        meta={
          isNew
            ? "Save the settings first — questions attach to a saved draft."
            : detail.data
              ? `${detail.data.exam.subject_name} · ${detail.data.exam.question_count} questions · ${formatMarks(detail.data.exam.total_marks)} marks`
              : undefined
        }
      />

      <FixtureBanner />

      {!isNew && detail.data?.exam ? (
        <StudentLinkCard exam={detail.data.exam} />
      ) : null}

      {!isNew && detail.loading ? (
        <Loading />
      ) : !isNew && detail.error ? (
        <ErrorNote error={detail.error} />
      ) : (
        <>
          <SettingsCard
            // Re-seeds the form whenever the saved settings change underneath
            // it — after a save of its own, and after an "extend exam" that
            // rewrote the duration and the window from the actions card.
            key={detail.data ? settingsKey(detail.data.exam) : "new"}
            exam={detail.data?.exam ?? null}
            onCreated={(id) => navigate(`/admin/exams/${id}`, { replace: true })}
            onSaved={() => detail.reload()}
          />

          {!isNew && detail.data ? (
            <>
              {/* Who the paper is for sits directly under the settings: it is
                  a setting in every sense except that it lives in its own
                  table, and it has to be decided before the exam is
                  published rather than discovered afterwards. */}
              <ExamRosterCard examId={detail.data.exam.id} />

              {detail.data.exam.status !== "draft" ? (
                <ExamActionsCard
                  exam={detail.data.exam}
                  onChanged={() => detail.reload()}
                />
              ) : null}

              <PaperSection
                exam={detail.data.exam}
                questions={detail.data.questions}
                onChanged={() => detail.reload()}
                onError={(message) =>
                  toast({ tone: "error", title: "That did not work.", description: message })
                }
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Settings                                                            */
/* ------------------------------------------------------------------ */

type ExamRow = NonNullable<
  Awaited<ReturnType<typeof fetchExamDetail>>
>["exam"];

/**
 * The settings that `PATCH /api/exams/:id` accepts, in the shape the form
 * holds them. Kept as one object so "what did the admin actually change" is a
 * comparison against the saved exam rather than ten booleans.
 */
interface SettingsDraft {
  subject_id: string;
  title: string;
  timing_mode: "from_start" | "scheduled";
  scheduled_at: number | null;
  available_from: number | null;
  available_until: number | null;
  duration_min: number;
  min_submit_after_min: number;
  show_answers_after_submit: boolean;
  syllabus_visible: boolean;
  collect_answer_sheets: boolean;
  require_phone: boolean;
  allow_scratchpad: boolean;
  covers_weeks: number[];
}

/** Human names for the changed-fields line on the save confirmation. */
const SETTING_LABEL: Record<keyof SettingsDraft, string> = {
  subject_id: "subject",
  title: "title",
  timing_mode: "timing mode",
  scheduled_at: "scheduled sitting",
  available_from: "can start from",
  available_until: "must start by",
  duration_min: "duration",
  min_submit_after_min: "submit lock",
  show_answers_after_submit: "answers after submit",
  syllabus_visible: "topic list for students",
  collect_answer_sheets: "answer-sheet photos",
  require_phone: "phone side camera",
  allow_scratchpad: "rough-work pad",
  covers_weeks: "weeks covered",
};

const SETTING_KEYS = Object.keys(SETTING_LABEL) as Array<keyof SettingsDraft>;

/**
 * Identity of the *saved* settings, used as the form's React key.
 *
 * The form is uncontrolled with respect to the server: it seeds itself from the
 * exam once and then belongs to the admin typing into it. That is right until
 * the saved values move — a save of its own, or an "extend exam" that rewrote
 * the duration and the window — at which point a form still showing the old
 * numbers is lying. Keying on the values re-seeds it exactly then and never in
 * the middle of someone's typing.
 */
function settingsKey(exam: ExamRow): string {
  return [exam.id, ...SETTING_KEYS.map((key) => String(exam[key]))].join("|");
}

/**
 * The date inputs hold whole minutes, so a time saved by a script with seconds
 * in it would otherwise read as an unsaved edit the admin never made.
 */
const MINUTE_KEYS = new Set<keyof SettingsDraft>([
  "scheduled_at",
  "available_from",
  "available_until",
]);

function toMinute(value: unknown): unknown {
  return typeof value === "number" ? Math.floor(value / 60_000) : value;
}

/** Which saved settings the draft disagrees with. */
function changedSettings(
  exam: ExamRow,
  draft: SettingsDraft,
): Array<keyof SettingsDraft> {
  return SETTING_KEYS.filter((key) => {
    const before = exam[key];
    const after = draft[key];
    if (Array.isArray(before) && Array.isArray(after)) {
      return (
        before.length !== after.length ||
        before.some((value, i) => value !== after[i])
      );
    }
    if (MINUTE_KEYS.has(key)) return toMinute(before) !== toMinute(after);
    return before !== after;
  });
}

function SettingsCard({
  exam,
  onCreated,
  onSaved,
}: {
  exam: ExamRow | null;
  onCreated: (id: string) => void;
  onSaved: () => void;
}) {
  const catalog = useCatalog();
  const { toast } = useToast();

  const [subjectId, setSubjectId] = useState(
    exam?.subject_id ?? catalog.subjects[0]?.id ?? "",
  );
  const [title, setTitle] = useState(exam?.title ?? "");
  const [timingMode, setTimingMode] = useState<"from_start" | "scheduled">(
    exam?.timing_mode ?? "from_start",
  );
  const [scheduledAt, setScheduledAt] = useState(
    toDateTimeLocal(exam?.scheduled_at ?? null),
  );
  const [availableFrom, setAvailableFrom] = useState(
    toDateTimeLocal(exam?.available_from ?? null),
  );
  const [availableUntil, setAvailableUntil] = useState(
    toDateTimeLocal(exam?.available_until ?? null),
  );
  const [duration, setDuration] = useState(String(exam?.duration_min ?? 60));
  const [minSubmitAfter, setMinSubmitAfter] = useState(
    String(exam?.min_submit_after_min ?? 0),
  );
  const [requirePhone, setRequirePhone] = useState(exam?.require_phone ?? false);
  const [allowScratchpad, setAllowScratchpad] = useState(
    exam?.allow_scratchpad ?? true,
  );
  const [collectAnswerSheets, setCollectAnswerSheets] = useState(
    exam?.collect_answer_sheets ?? false,
  );
  const [syllabusVisible, setSyllabusVisible] = useState(
    exam?.syllabus_visible ?? false,
  );
  const [showAnswersAfterSubmit, setShowAnswersAfterSubmit] = useState(
    exam?.show_answers_after_submit ?? true,
  );
  const [weeks, setWeeks] = useState<number[]>(exam?.covers_weeks ?? []);
  const [busy, setBusy] = useState(false);

  const scheduled = timingMode === "scheduled";
  // A submit lock longer than the paper is a mistake the server would reject
  // with a 400, on create and on edit alike.
  const minSubmitInvalid =
    Number(minSubmitAfter) > Number(duration) && Number(duration) > 0;

  const availableWeeks = catalog.weeksBySubject.get(subjectId) ?? [];

  /**
   * True once somebody is actually sitting this exam. It is the same condition
   * that freezes the paper server-side, and it is the only thing that locks a
   * setting: the subject decides which questions the paper could hold, and the
   * timing mode decides what "time left" means to a clock already running.
   */
  const sittingStarted = exam?.frozen ?? false;

  const draft: SettingsDraft = {
    subject_id: subjectId,
    title: title.trim(),
    timing_mode: timingMode,
    // Only a fixed sitting has a sitting time. The start window applies to both
    // modes — the API's `availabilityWindow` enforces `available_from` and
    // `available_until` on a from_start exam too ("start any time this week").
    // Hiding them here once left an expired deadline nobody could see turning
    // every student away with "Missed".
    scheduled_at: scheduled ? fromDateTimeLocal(scheduledAt) : null,
    available_from: fromDateTimeLocal(availableFrom),
    available_until: fromDateTimeLocal(availableUntil),
    duration_min: Number(duration),
    min_submit_after_min: Number(minSubmitAfter) || 0,
    show_answers_after_submit: showAnswersAfterSubmit,
    syllabus_visible: syllabusVisible,
    collect_answer_sheets: collectAnswerSheets,
    require_phone: requirePhone,
    allow_scratchpad: allowScratchpad,
    covers_weeks: weeks,
  };

  // A reversed window makes the exam impossible to start, and the API takes it.
  const windowReversed =
    draft.available_from !== null &&
    draft.available_until !== null &&
    draft.available_from >= draft.available_until;
  // The deadline that turned students away unseen. Not an error — closing an
  // exam by letting its window lapse is legitimate — but it must be loud.
  const windowClosed =
    draft.available_until !== null && draft.available_until < Date.now();

  const changed = exam ? changedSettings(exam, draft) : SETTING_KEYS;
  const changedText = changed.map((key) => SETTING_LABEL[key]).join(", ");
  const invalid =
    busy ||
    minSubmitInvalid ||
    windowReversed ||
    title.trim() === "" ||
    !subjectId ||
    !(Number(duration) > 0);

  const create = async () => {
    setBusy(true);
    try {
      const created = await createExam(draft);
      toast({ tone: "success", title: "Draft saved. Now add questions." });
      onCreated(created.id);
    } catch (err) {
      toast({
        tone: "error",
        title: "Could not save the exam.",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  /** Sends only what moved, so the log and the request both say what changed. */
  const saveChanges = async () => {
    if (!exam || changed.length === 0) return;
    const patch: Parameters<typeof updateExam>[1] = {};
    for (const key of changed) {
      if (key === "subject_id") patch.subject_id = draft.subject_id;
      if (key === "title") patch.title = draft.title;
      if (key === "timing_mode") patch.timing_mode = draft.timing_mode;
      if (key === "scheduled_at") patch.scheduled_at = draft.scheduled_at;
      if (key === "available_from") patch.available_from = draft.available_from;
      if (key === "available_until")
        patch.available_until = draft.available_until;
      if (key === "duration_min") patch.duration_min = draft.duration_min;
      if (key === "min_submit_after_min")
        patch.min_submit_after_min = draft.min_submit_after_min;
      if (key === "show_answers_after_submit")
        patch.show_answers_after_submit = draft.show_answers_after_submit;
      if (key === "syllabus_visible")
        patch.syllabus_visible = draft.syllabus_visible;
      if (key === "collect_answer_sheets")
        patch.collect_answer_sheets = draft.collect_answer_sheets;
      if (key === "require_phone") patch.require_phone = draft.require_phone;
      if (key === "allow_scratchpad")
        patch.allow_scratchpad = draft.allow_scratchpad;
      if (key === "covers_weeks") patch.covers_weeks = draft.covers_weeks;
    }

    setBusy(true);
    try {
      await updateExam(exam.id, patch);
      toast({
        tone: "success",
        title: "Settings saved.",
        description: `Changed: ${changedText}.`,
      });
      onSaved();
    } catch (err) {
      toast({
        tone: "error",
        title: "Could not save the settings.",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Moves the exam's status. Used to schedule a draft and to reopen a finished
   * exam; both go through the same endpoint, which is the one place the API
   * decides whether a transition is legal.
   */
  const moveTo = async (
    status: "scheduled",
    messages: { success: string; failure: string },
  ) => {
    if (!exam) return;
    setBusy(true);
    try {
      await publishExam(exam.id, {
        // Only a fixed-time exam needs a date; a from_start exam is startable
        // on demand, so publishing it just makes it available.
        scheduled_at:
          draft.timing_mode === "scheduled" ? draft.scheduled_at : null,
        status,
      });
      toast({ tone: "success", title: messages.success });
      onSaved();
    } catch (err) {
      toast({
        tone: "error",
        title: messages.failure,
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  const pending = changed.length > 0;
  const needsDate =
    draft.timing_mode === "scheduled" && draft.scheduled_at === null;

  /**
   * One line under the buttons that says what the exam is, or why the button
   * next to it will not press. A disabled control with no explanation is the
   * state this whole screen was rebuilt to get out of.
   */
  const footerNote = !exam
    ? ""
    : pending
      ? `Unsaved: ${changedText}. Save them before changing the status.`
      : needsDate && (exam.status === "draft" || exam.status === "done")
        ? "Set a date and time above first — a fixed-window exam cannot go live without one."
        : exam.status === "draft"
          ? "Draft. Attach questions, then schedule it."
          : exam.status === "scheduled"
            ? exam.timing_mode === "scheduled"
              ? "Scheduled. Students can start it inside its window."
              : windowClosed
                ? "Published, but its window has closed — nobody can start it."
                : exam.available_from !== null || exam.available_until !== null
                  ? "Published. Students can start it inside its window."
                  : "Published. Students can start it whenever they open it."
            : exam.status === "live"
              ? "Live. At least one attempt is under way."
              : "Finished. Reopening it leaves every attempt exactly as it is — answers, recordings and grades all stay.";

  return (
    <Card>
      <CardHeader
        title="Settings"
        description={
          sittingStarted
            ? "This exam is being sat. The paper is locked; the settings below are not."
            : "Subject, timing and the syllabus the paper draws from."
        }
        actions={
          exam ? (
            <Badge tone={exam.status === "scheduled" ? "accent" : "neutral"}>
              {exam.status}
            </Badge>
          ) : null
        }
      />

      <CardBody className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1">
            <Select
              label="Subject"
              value={subjectId}
              disabled={sittingStarted}
              onChange={(e) => {
                setSubjectId(e.target.value);
                setWeeks([]);
              }}
            >
              {catalog.subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            {sittingStarted ? (
              <p className="text-2xs text-ink-faint">
                Locked while this exam is being sat — the paper's questions
                belong to this subject.
              </p>
            ) : null}
          </div>

          <Input
            label="Title"
            value={title}
            placeholder="Maths 1 — Quiz 2"
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="flex flex-col gap-1">
            <Select
              label="Timing"
              value={timingMode}
              disabled={sittingStarted}
              onChange={(e) =>
                setTimingMode(e.target.value as "from_start" | "scheduled")
              }
            >
              <option value="from_start">Timer starts when they begin</option>
              <option value="scheduled">Fixed scheduled window</option>
            </Select>
            {sittingStarted ? (
              <p className="text-2xs text-ink-faint">
                Locked while this exam is being sat — it would move the
                goalposts of a clock already running.
              </p>
            ) : null}
          </div>

          <Input
            label="Duration (minutes)"
            type="number"
            min={1}
            max={600}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            hint={
              sittingStarted
                ? "Editable, and it moves the deadline for anyone sitting now."
                : undefined
            }
          />

          <Input
            label="Submit unlocks after (min)"
            type="number"
            min={0}
            max={600}
            value={minSubmitAfter}
            onChange={(e) => setMinSubmitAfter(e.target.value)}
            error={minSubmitInvalid ? "Must not exceed the duration." : null}
            hint="0 = submit anytime. Set e.g. 40 for an invigilated sitting."
          />
        </div>

        <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-sunken p-3">
          <div
            className={
              scheduled ? "grid gap-3 sm:grid-cols-3" : "grid gap-3 sm:grid-cols-2"
            }
          >
            {scheduled ? (
              <Input
                label="Scheduled sitting"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                hint="The fixed time the paper is sat."
              />
            ) : null}
            <Input
              label="Can start from"
              type="datetime-local"
              value={availableFrom}
              onChange={(e) => setAvailableFrom(e.target.value)}
              hint="Optional. The earliest a student may begin."
            />
            <Input
              label="Must start by"
              type="datetime-local"
              value={availableUntil}
              onChange={(e) => setAvailableUntil(e.target.value)}
              hint="Optional. After this nobody can start."
              error={
                windowReversed
                  ? "Must be after “Can start from”."
                  : windowClosed
                    ? "This is in the past — nobody can start this exam now."
                    : null
              }
            />
          </div>
          {scheduled ? null : (
            <p className="text-2xs text-ink-muted">
              {availableFrom || availableUntil
                ? "A student can start this exam any time inside the window above; the "
                : "A student can start this exam whenever they open it; the "}
              <span className="tabular text-ink">{duration || "—"}</span>-minute
              countdown begins the moment they do.
            </p>
          )}
        </div>

        <label className="flex items-start gap-2.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
          <input
            type="checkbox"
            checked={showAnswersAfterSubmit}
            onChange={(e) => setShowAnswersAfterSubmit(e.target.checked)}
            className="mt-0.5 size-4 rounded-xs border-border-strong text-accent focus:ring-accent disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-xs font-medium text-ink">
              Show answers &amp; solutions after submitting
            </span>
            <span className="block text-2xs text-ink-muted">
              On for practice — the student sees the correct answer, their
              result and the worked solution once submitted. Turn it off to
              withhold the key.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
          <input
            type="checkbox"
            checked={syllabusVisible}
            onChange={(e) => setSyllabusVisible(e.target.checked)}
            className="mt-0.5 size-4 rounded-xs border-border-strong text-accent focus:ring-accent disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-xs font-medium text-ink">
              Tell students which topics this exam covers
            </span>
            <span className="block text-2xs text-ink-muted">
              The topic names appear on their dashboard as soon as the exam is
              scheduled, so they know what to revise before the day. Names
              only — never the questions, counts or difficulty.
              <br />
              Leave off for a diagnostic: announcing the topics changes what
              you are measuring. The list is read from the questions on the
              paper, so it stays correct if you edit it.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
          <input
            type="checkbox"
            checked={allowScratchpad}
            onChange={(e) => setAllowScratchpad(e.target.checked)}
            className="mt-0.5 size-4 rounded-xs border-border-strong text-accent focus:ring-accent disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-xs font-medium text-ink">
              Give the student a rough-work pad
            </span>
            <span className="block text-2xs text-ink-muted">
              On by default — with the answer-sheet step gone there is nowhere
              else to work something out. A small draggable panel they can write
              or draw in. Nothing is uploaded: the screen recording already
              captures the working, in context, so you can watch it there.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
          <input
            type="checkbox"
            checked={requirePhone}
            onChange={(e) => setRequirePhone(e.target.checked)}
            className="mt-0.5 size-4 rounded-xs border-border-strong text-accent focus:ring-accent disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-xs font-medium text-ink">
              Require a phone as the side camera
            </span>
            <span className="block text-2xs text-ink-muted">
              Off by default. The phone is a third video stream on the same home
              connection, so on a slow link asking for it can cost you the
              screen recording — the one nothing else can replace. Leave it off
              unless you specifically want the side view; setup then skips
              pairing and placement entirely.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
          <input
            type="checkbox"
            checked={collectAnswerSheets}
            onChange={(e) => setCollectAnswerSheets(e.target.checked)}
            className="mt-0.5 size-4 rounded-xs border-border-strong text-accent focus:ring-accent disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-xs font-medium text-ink">
              Ask for photos of the rough sheets after submitting
            </span>
            <span className="block text-2xs text-ink-muted">
              Only for a written paper, where the working on the sheets is the
              evidence. A multiple-choice or coding exam produces no sheets, and
              the step would ask the student to photograph nothing at the exact
              moment they should be leaving the page open for the recording to
              finish uploading.
            </span>
          </span>
        </label>

        <div>
          <p className="mb-1.5 text-xs font-medium text-ink-muted">
            Weeks covered
            {weeks.length > 0 ? (
              <span className="ml-1.5 font-normal text-ink-faint">
                {summariseWeeks(weeks)}
              </span>
            ) : null}
          </p>
          {availableWeeks.length === 0 ? (
            <p className="text-xs text-ink-faint">
              This subject has no topics with a week number yet.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {availableWeeks.map((week) => {
                const on = weeks.includes(week);
                return (
                  <button
                    key={week}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setWeeks((prev) =>
                        on ? prev.filter((w) => w !== week) : [...prev, week].sort((a, b) => a - b),
                      )
                    }
                    className={
                      on
                        ? "tabular h-7 w-9 rounded-sm border border-accent bg-accent-soft text-2xs font-medium text-accent transition-colors disabled:opacity-60"
                        : "tabular h-7 w-9 rounded-sm border border-border-strong bg-surface text-2xs text-ink-muted transition-colors hover:bg-surface-sunken disabled:opacity-60"
                    }
                  >
                    {week}
                  </button>
                );
              })}
            </div>
          )}
          {exam !== null ? (
            <p className="mt-1.5 text-2xs text-ink-faint">
              Weeks describe what the paper was built from; editing them does
              not re-pick any questions.
            </p>
          ) : null}
        </div>
      </CardBody>

      <CardFooter>
        {!exam ? (
          <Button
            variant="primary"
            disabled={invalid}
            onClick={() => void create()}
          >
            Save draft
          </Button>
        ) : (
          <>
            <p className="mr-auto text-xs text-ink-muted">{footerNote}</p>

            <Button
              variant={changed.length > 0 ? "primary" : "secondary"}
              disabled={invalid || changed.length === 0}
              onClick={() => void saveChanges()}
            >
              Save changes
            </Button>

            {exam.status === "draft" ? (
              <Button
                variant="primary"
                disabled={busy || pending || exam.question_count === 0 || needsDate}
                onClick={() =>
                  void moveTo("scheduled", {
                    success:
                      exam.timing_mode === "scheduled"
                        ? "Exam scheduled."
                        : "Exam published.",
                    failure: "Could not schedule the exam.",
                  })
                }
              >
                {exam.timing_mode === "scheduled" ? "Schedule exam" : "Publish exam"}
              </Button>
            ) : exam.status === "done" ? (
              <Button
                variant="primary"
                icon={<RefreshCw />}
                disabled={busy || pending || needsDate}
                title="Puts the exam back to scheduled. Existing attempts keep their answers and grades."
                onClick={() =>
                  void moveTo("scheduled", {
                    success: "Exam reopened. Existing attempts are untouched.",
                    failure: "Could not reopen the exam.",
                  })
                }
              >
                Reopen exam
              </Button>
            ) : null}
          </>
        )}
      </CardFooter>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Paper                                                               */
/* ------------------------------------------------------------------ */

function PaperSection({
  exam,
  questions,
  onChanged,
  onError,
}: {
  exam: ExamRow;
  questions: ExamQuestionDetail[];
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const { toast } = useToast();
  const [previewing, setPreviewing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const totalMarks = questions.reduce((sum, q) => sum + q.marks, 0);
  const byType = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const q of questions) acc[q.question.type] = (acc[q.question.type] ?? 0) + 1;
    return acc;
  }, [questions]);

  const mutate = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  /** Rebuilds the paper without one question — there is no per-slot DELETE. */
  const removeAt = (questionId: string) =>
    mutate(() =>
      attachQuestions(
        exam.id,
        questions
          .filter((q) => q.question_id !== questionId)
          .map((q) => ({
            question_id: q.question_id,
            marks: q.marks,
            negative_marks: q.negative_marks,
          })),
        true,
      ),
    );

  const move = (index: number, delta: number) => {
    const next = [...questions];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    return mutate(() =>
      attachQuestions(
        exam.id,
        next.map((q) => ({
          question_id: q.question_id,
          marks: q.marks,
          negative_marks: q.negative_marks,
        })),
        true,
      ),
    );
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Paper"
          description={
            questions.length === 0
              ? "Empty. Auto-pick from the bank, or add questions by hand."
              : `${questions.length} questions · ${formatMarks(totalMarks)} marks · ${Object.entries(
                  byType,
                )
                  .map(([type, n]) => `${n} ${type}`)
                  .join(", ")}`
          }
          actions={
            <div className="flex gap-2">
              <Button
                size="sm"
                icon={<Eye />}
                disabled={questions.length === 0}
                onClick={() => setPreviewing(true)}
              >
                Preview paper
              </Button>
              <Button
                size="sm"
                icon={<Plus />}
                disabled={exam.frozen}
                onClick={() => setPicking(true)}
              >
                Add by hand
              </Button>
            </div>
          }
        />

        {exam.frozen ? (
          <div className="flex items-center gap-2 border-b border-border bg-surface-sunken px-4 py-2">
            <Lock className="size-3.5 text-ink-faint" />
            <p className="text-xs text-ink-muted">
              The paper is frozen because a student has started this exam.
            </p>
          </div>
        ) : null}

        <Table>
          <THead>
            <tr>
              <TH align="right">#</TH>
              <TH>Question</TH>
              <TH>Topic</TH>
              <TH align="right">Week</TH>
              <TH>Type</TH>
              <TH align="right">Marks</TH>
              <TH align="right">Negative</TH>
              <TH />
            </tr>
          </THead>
          <TBody>
            {questions.length === 0 ? (
              <TableEmpty colSpan={8}>No questions on this paper yet.</TableEmpty>
            ) : (
              questions.map((slot, i) => (
                <TR key={slot.question_id}>
                  <TD align="right" className="tabular text-ink-faint">
                    {i + 1}
                  </TD>
                  <TD className="max-w-sm">
                    <span className="block truncate">
                      {plainSummary(slot.question.body_md)}
                    </span>
                  </TD>
                  <TD className="text-ink-muted">{slot.question.topic_name}</TD>
                  <TD align="right" className="tabular">
                    {slot.question.week_no ?? "—"}
                  </TD>
                  <TD>{slot.question.type}</TD>
                  <TD align="right" className="tabular">
                    {formatMarks(slot.marks)}
                  </TD>
                  <TD align="right" className="tabular text-ink-muted">
                    {slot.negative_marks > 0 ? `−${formatMarks(slot.negative_marks)}` : "—"}
                  </TD>
                  <TD align="right">
                    <div className="flex justify-end gap-0.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<ArrowUp />}
                        disabled={busy || exam.frozen || i === 0}
                        onClick={() => void move(i, -1)}
                        aria-label="Move up"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<ArrowDown />}
                        disabled={busy || exam.frozen || i === questions.length - 1}
                        onClick={() => void move(i, 1)}
                        aria-label="Move down"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 />}
                        disabled={busy || exam.frozen}
                        onClick={() => void removeAt(slot.question_id)}
                        aria-label="Remove from paper"
                      />
                    </div>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </Card>

      {!exam.frozen ? (
        <AutoPickCard
          examId={exam.id}
          onApplied={(added) => {
            toast({
              tone: "success",
              title: `Added ${added} question${added === 1 ? "" : "s"}.`,
            });
            onChanged();
          }}
          onError={onError}
        />
      ) : null}

      <PaperPreviewDialog
        open={previewing}
        onClose={() => setPreviewing(false)}
        title={exam.title}
        questions={questions}
      />

      <HandPickDialog
        open={picking}
        onClose={() => setPicking(false)}
        subjectId={exam.subject_id}
        alreadyOn={new Set(questions.map((q) => q.question_id))}
        onAdd={(picked) =>
          mutate(async () => {
            await attachQuestions(exam.id, picked);
            setPicking(false);
          })
        }
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Auto-pick                                                           */
/* ------------------------------------------------------------------ */

const BUCKET_LABEL: Record<string, string> = {
  weak: "Weak topics",
  recent: "Recent weeks",
  revision: "Revision",
};

function AutoPickCard({
  examId,
  onApplied,
  onError,
}: {
  examId: string;
  onApplied: (added: number) => void;
  onError: (message: string) => void;
}) {
  const [count, setCount] = useState("10");
  const [marks, setMarks] = useState("2");
  const [negative, setNegative] = useState("0.66");
  const [type, setType] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [replace, setReplace] = useState(false);
  const [preview, setPreview] = useState<AutoPickResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const request = {
    count: Math.max(1, Number(count) || 1),
    marks: Number(marks) || 1,
    negative_marks: Math.max(0, Number(negative) || 0),
    ...(type ? { type: type as "MCQ" | "MSQ" | "NAT" } : {}),
    ...(difficulty ? { difficulty: difficulty as "easy" | "medium" | "hard" } : {}),
    replace,
  };

  const run = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const result = await autoPick(examId, { ...request, dry_run: dryRun });
      if (dryRun) {
        setPreview(result);
      } else {
        setPreview(null);
        onApplied(result.picked.length);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Auto-pick failed.");
    } finally {
      setBusy(false);
    }
  };

  const byBucket = useMemo(() => {
    const acc: Record<string, number> = { weak: 0, recent: 0, revision: 0 };
    for (const pick of preview?.picked ?? []) acc[pick.bucket] = (acc[pick.bucket] ?? 0) + 1;
    return acc;
  }, [preview]);

  const missing = Object.entries(preview?.shortfall ?? {}).filter(([, n]) => n > 0);

  return (
    <Card>
      <CardHeader
        title="Auto-pick"
        description="40% topics students have got wrong before, 40% the newest covered weeks, 20% revision. Buckets that run dry spill into the next one."
      />

      <CardBody className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Input
            label="How many"
            type="number"
            min={1}
            max={200}
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
          <Input
            label="Marks each"
            type="number"
            step="any"
            min={0.5}
            value={marks}
            onChange={(e) => setMarks(e.target.value)}
          />
          <Input
            label="Negative marks"
            type="number"
            step="any"
            min={0}
            value={negative}
            onChange={(e) => setNegative(e.target.value)}
            hint="0 for no negative marking."
          />
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Any</option>
            <option value="MCQ">MCQ</option>
            <option value="MSQ">MSQ</option>
            <option value="NAT">NAT</option>
          </Select>
          <Select
            label="Difficulty"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
          >
            <option value="">Any</option>
            {Difficulty.options.map((value) => (
              <option key={value} value={value}>
                {labelFor(DIFFICULTY_LABEL, value)}
              </option>
            ))}
          </Select>
        </div>

        <label className="flex items-center gap-2 text-xs text-ink-muted">
          <input
            type="checkbox"
            checked={replace}
            onChange={(e) => setReplace(e.target.checked)}
            className="size-3.5 accent-accent"
          />
          Replace the current paper instead of adding to it
        </label>

        {preview ? (
          <div className="space-y-2 rounded-md border border-border bg-surface-sunken p-3">
            <p className="text-xs font-medium text-ink">
              Would pick {preview.picked.length} of {preview.requested} requested
            </p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(byBucket).map(([bucket, n]) => (
                <Badge key={bucket}>
                  {BUCKET_LABEL[bucket] ?? bucket}: {n}
                </Badge>
              ))}
            </div>
            {missing.length > 0 ? (
              <p className="text-xs text-review">
                The bank is short:{" "}
                {missing
                  .map(([bucket, n]) => `${n} in ${BUCKET_LABEL[bucket] ?? bucket}`)
                  .join(", ")}
                .
              </p>
            ) : null}
          </div>
        ) : null}
      </CardBody>

      <CardFooter>
        <Button disabled={busy} onClick={() => void run(true)} icon={<Eye />}>
          Preview picks
        </Button>
        <Button
          variant="primary"
          disabled={busy}
          onClick={() => void run(false)}
          icon={<Sparkles />}
        >
          Add to paper
        </Button>
      </CardFooter>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Hand-picking                                                        */
/* ------------------------------------------------------------------ */

function HandPickDialog({
  open,
  onClose,
  subjectId,
  alreadyOn,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  subjectId: string;
  alreadyOn: Set<string>;
  onAdd: (
    picked: Array<{ question_id: string; marks: number; negative_marks: number }>,
  ) => void;
}) {
  const catalog = useCatalog();
  const [filters, setFilters] = useState<QuestionFilterValues>({
    ...EMPTY_FILTERS,
    subject_id: subjectId,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [marks, setMarks] = useState("2");
  const [negative, setNegative] = useState("0.66");

  // Only approved questions may be attached — the API rejects anything else.
  const query = { ...toQuery(filters), status: "approved" as const };
  const state = useAsync(
    () => fetchQuestions({ ...query, limit: 100 }),
    [JSON.stringify(query), open],
    { enabled: open },
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add questions"
      description="Approved questions only."
      className="w-[min(64rem,calc(100vw-2rem))]"
      footer={
        <>
          <span className="tabular mr-auto text-2xs text-ink-faint">
            {selected.size} selected
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={() => {
              onAdd(
                [...selected].map((id) => ({
                  question_id: id,
                  marks: Number(marks) || 1,
                  negative_marks: Math.max(0, Number(negative) || 0),
                })),
              );
              setSelected(new Set());
            }}
          >
            Add {selected.size > 0 ? selected.size : ""} to paper
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <QuestionFilters
          catalog={catalog}
          values={filters}
          onChange={setFilters}
          showStatus={false}
        />

        <div className="flex items-end gap-2">
          <Input
            label="Marks each"
            type="number"
            step="any"
            value={marks}
            onChange={(e) => setMarks(e.target.value)}
            className="w-28"
          />
          <Input
            label="Negative marks"
            type="number"
            step="any"
            min={0}
            value={negative}
            onChange={(e) => setNegative(e.target.value)}
            className="w-28"
          />
        </div>

        <div className="max-h-96 overflow-y-auto rounded-md border border-border">
          {state.loading ? (
            <Loading />
          ) : state.error ? (
            <div className="p-3">
              <ErrorNote error={state.error} />
            </div>
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH />
                  <TH>Question</TH>
                  <TH>Topic</TH>
                  <TH align="right">Week</TH>
                  <TH>Type</TH>
                  <TH>Difficulty</TH>
                </tr>
              </THead>
              <TBody>
                {(state.data?.questions ?? []).length === 0 ? (
                  <TableEmpty colSpan={6}>No approved questions match.</TableEmpty>
                ) : (
                  state.data!.questions.map((question) => {
                    // Already attached, or something the API will refuse to
                    // attach (unverified NAT key, missing figure).
                    const blockers = blockersFor(question);
                    const on = alreadyOn.has(question.id) || blockers.length > 0;
                    return (
                      <TR
                        key={question.id}
                        selected={selected.has(question.id)}
                        className={on ? "opacity-50" : "cursor-pointer"}
                        onClick={() => (on ? undefined : toggle(question.id))}
                      >
                        <TD>
                          <input
                            type="checkbox"
                            className="size-3.5 accent-accent"
                            checked={on || selected.has(question.id)}
                            disabled={on}
                            onChange={() => toggle(question.id)}
                            aria-label={
                              blockers.length > 0
                                ? blockers.join(", ")
                                : on
                                  ? "Already on the paper"
                                  : "Select question"
                            }
                          />
                        </TD>
                        <TD className="max-w-sm">
                          <span className="block truncate">
                            {plainSummary(question.body_md)}
                          </span>
                          {blockers.length > 0 ? (
                            <span className="text-2xs text-violation">
                              {blockers.join(" · ")}
                            </span>
                          ) : null}
                        </TD>
                        <TD className="text-ink-muted">{question.topic_name}</TD>
                        <TD align="right" className="tabular">
                          {question.week_no ?? "—"}
                        </TD>
                        <TD>{question.type}</TD>
                        <TD className="text-ink-muted">{question.difficulty}</TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Preview                                                             */
/* ------------------------------------------------------------------ */

/** The paper as the student will meet it — no answers unless asked for. */
function PaperPreviewDialog({
  open,
  onClose,
  title,
  questions,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  questions: ExamQuestionDetail[];
}) {
  const [showAnswers, setShowAnswers] = useState(false);
  const totalMarks = questions.reduce((sum, q) => sum + q.marks, 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={`${questions.length} questions · ${formatMarks(totalMarks)} marks`}
      className="w-[min(56rem,calc(100vw-2rem))]"
      footer={
        <>
          <label className="mr-auto flex items-center gap-2 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={showAnswers}
              onChange={(e) => setShowAnswers(e.target.checked)}
              className="size-3.5 accent-accent"
            />
            Show answers and solutions
          </label>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      <div className="max-h-[70vh] space-y-6 overflow-y-auto">
        {questions.map((slot, i) => (
          <div key={slot.question_id} className="space-y-2">
            <div className="flex items-baseline justify-between gap-4 border-b border-border pb-1.5">
              <span className="text-xs font-medium text-ink">
                Question {i + 1}
              </span>
              <span className="tabular text-2xs text-ink-muted">
                +{formatMarks(slot.marks)}
                {slot.negative_marks > 0 ? ` / −${formatMarks(slot.negative_marks)}` : ""}
              </span>
            </div>
            {showAnswers ? <QuestionMeta question={slot.question} /> : null}
            <QuestionView question={slot.question} showAnswer={showAnswers} />
          </div>
        ))}
      </div>
    </Dialog>
  );
}

/**
 * The link to hand a student.
 *
 * `/student/exam/:examId/setup` was already a per-exam URL — there was simply
 * no way to get hold of it without typing it out, so in practice the only way
 * to point a student at an exam was "log in and look for it".
 *
 * The panel states what the link will actually do RIGHT NOW rather than just
 * offering it. A link to a draft exam opens a page that refuses to start, and
 * a link sent to someone not on the roster does the same — both look like the
 * link is broken. Saying so here is the difference between sending a working
 * link and finding out from the student that it did not work.
 */
function StudentLinkCard({ exam }: { exam: ExamRow }) {
  const url = `${location.origin}/student/exam/${exam.id}/setup`;

  // Ordered by what stops the student first. Only the first one is shown:
  // three warnings at once reads as noise and none of them get fixed.
  const blocker =
    exam.status === "draft"
      ? "This exam is still a draft — the link will open, but it will refuse to start. Schedule it first."
      : exam.status === "done"
        ? "This exam is finished. The link will open and refuse to start."
        : exam.question_count === 0
          ? "This exam has no questions yet, so the link cannot start anything."
          : null;

  return (
    <Card>
      <CardHeader
        title="Link for students"
        description="Send this to a student. It takes them straight into the checks for this exam — signing in first if they need to."
      />
      <CardBody className="space-y-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-2xs text-ink">
            {url}
          </code>
          <CopyButton value={url} label="Copy link" variant="primary" />
        </div>

        {blocker ? (
          <p className="flex items-start gap-1.5 rounded-md border border-warn/40 bg-warn-soft px-2.5 py-1.5 text-2xs text-warn">
            <TriangleAlert className="mt-px size-3.5 shrink-0" />
            {blocker}
          </p>
        ) : null}

        <p className="text-2xs text-ink-faint">
          {exam.assigned_count && exam.assigned_count > 0
            ? `Only the ${exam.assigned_count} assigned ${exam.assigned_count === 1 ? "student" : "students"} can start it — anyone else who opens the link is turned away.`
            : "Nobody is assigned, so any student who opens this can start it. Assign students below to restrict it."}
        </p>
      </CardBody>
    </Card>
  );
}
